import { type IRtiSettingsPersisted, ISettingsState } from 'model/interfaces';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import {
  defaultCalculationMethodology,
  migratePersistedCalculationMethodology,
} from 'utilities/rti-calculation-methodology';
import { normalizeRtiAggregatedCompensation } from 'utilities/rti-aggregated-compensation';
import { normalizeRtiEmployeeDataFields } from 'utilities/rti-employee-data-fields';

const rtiInitial = {
  rtiReportHeader: '',
  rtiReportFooter: '',
  rtiTemplateVersion: 1,
  rtiCalculationMethodology: defaultCalculationMethodology(),
  rtiEmployeeDataFields: normalizeRtiEmployeeDataFields(null),
  rtiComparisonGroup: {} as Record<string, unknown>,
  rtiAggregatedCompensation: normalizeRtiAggregatedCompensation(null),
  rtiCompanyWideReport: {} as Record<string, unknown>,
  rtiMinimumDataThreshold: {} as Record<string, unknown>,
  rtiTemplateLoaded: false,
  rtiIsEditAccess: null as boolean | null,
};

const initialState = {
  ...rtiInitial,
  isPayGapsPositive: null,
  signThreshold: null,
  targetPayGap: null,
  maxIncrement: null,
};

export const useSettingsStore = create<ISettingsState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        updatePayGapsPositive: (
          isPayGapsPositive: boolean,
          signThreshold: string,
          targetPayGap: string,
          maxIncrement: string,
        ) => set({ isPayGapsPositive, signThreshold, targetPayGap, maxIncrement }),
        updateRtiSettings: (patch: Partial<IRtiSettingsPersisted>) =>
          set((state) => ({ ...state, ...patch })),
      }),
      {
        name: 'kf1-settingsStore', // Key name in sessionStorage
        storage: createJSONStorage((): Storage => window.sessionStorage), // Persist to sessionStorage
        merge: (persistedState, currentState) => {
          const partial = persistedState as Partial<ISettingsState>;
          const merged = { ...currentState, ...partial };
          if (merged.rtiCalculationMethodology != null) {
            merged.rtiCalculationMethodology = migratePersistedCalculationMethodology(
              merged.rtiCalculationMethodology,
            );
          }
          return merged;
        },
      },
    ),
  ),
);
